import PropTypes from 'prop-types';
import clone from 'lodash/clone';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import {
    ButtonsContainer,
    Col,
    Row
} from './styled';
import {
    ConfirmationModal,
    ErrorMessage,
    FormLayout
} from 'views/components';
import {
    ExpandButton,
    FormGroup,
    Input,
    TagList
} from 'mol-ui';
import React, { useEffect, useState } from 'react';
import {
    SpecialtyPicker,
    TurnTypePicker
} from 'views/pickers';

const { appointmentTypeSpecialties } = config;

const AppointmentTypeRegistration = ({
    appointmentType: selectedAppointmentType,
    createAppointmentType,
    deleteAppointmentType,
    getAppointmentType,
    location: {
        pathname
    },
    setTurnsAppointmentType: setSelectedAppointmentType,
    specialties = [],
    updateAppointmentType
}) => {

    const { t } = useTranslation();

    const mode = pathname.split('/')[3];
    const appointmentTypeId = pathname.split('/')[4];
    const createMode = mode === t('crear');
    const editMode = mode === t('editar');
    const viewMode = mode === t('ver');

    const [appointmentType, setAppointmentType] = useState({
        duration: 0,
        id: 0,
        name: '',
        type: 'presential'
    });
    const [errors, setErrors] = useState({});
    const [loaded, setLoaded] = useState(createMode);
    const [modalConfirm, setModalConfirm] = useState(false);
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);
    const [specialty_ids, setSpecialty_ids] = useState([]);

    useEffect(() => {
        if (!loaded) {
            setSpecialty_ids([]);
            setSelectedAppointmentType({});
            getAppointmentType(appointmentTypeId);
        }
    }, [appointmentTypeId, editMode, getAppointmentType, loaded, setSelectedAppointmentType, viewMode]);

    useEffect(() => {
        if (!loaded && !createMode && !isEmpty(selectedAppointmentType)) {
            setSpecialty_ids(selectedAppointmentType.specialties ? selectedAppointmentType.specialties.map(({ id }) => id) : []);
            setAppointmentType(selectedAppointmentType);
            setLoaded(true);
        }
    }, [createMode, loaded, selectedAppointmentType]);

    const addSpecialty = () => {
        if (!!selectedSpecialty) {
            setSpecialty_ids(specialty_ids => {
                specialty_ids.push(selectedSpecialty);
                return specialty_ids;
            });
            setSelectedSpecialty(null);
        }
    };

    const clearError = id => {
        setErrors(errors => {
            delete errors[id];
            return errors;
        });
    };

    const handleChange = (value, id) => {
        clearError(id);
        setAppointmentType(appointmentType => ({
            ...appointmentType,
            [id]: value
        }));
    };

    const handleSubmit = () => {
        const body = {
            ...appointmentType,
            specialty_ids
        };
        if (validate()) {
            if (createMode) {
                const newAppointmentType = clone(body);
                delete newAppointmentType.id;
                createAppointmentType(newAppointmentType);
            } else if (editMode) {
                updateAppointmentType({ body, id: appointmentTypeId });
            }
        }
    };

    const modalResponse = value => {
        if (value === 'confirm') {
            deleteAppointmentType(appointmentTypeId);
        }
        setModalConfirm(false);
    };

    const removeSpecialty = ({ value }) => {
        setSpecialty_ids(specialty_ids => specialty_ids.filter(id => id !== value));
    };

    const validate = () => {
        const { duration, name } = appointmentType;
        const newErrors = {};
        if (!duration || duration < 0) {
            newErrors.duration = t('Ingrese una duración válida');
        }
        if (!name) {
            newErrors.name = t('Requerido');
        }
        setErrors(newErrors);
        return isEmpty(newErrors);
    };

    const title = viewMode ? t('Ver tipo de consulta') : editMode ? t('Editar tipo de consulta') : t('Añadir tipo de consulta');
    const { duration, name, type } = appointmentType;
    const specialtyItems = specialties.filter(({ value }) => specialty_ids.includes(value));
    return (
        <FormLayout goBackTo={ t('/consultas/tipos/listar') } noOverflow={ true } title={ title }>
            <Row>
                <Col md="4" sm="6" xs="12">
                    <FormGroup label={ t('Tipo de turno') }>
                        <TurnTypePicker
                            disabled={ viewMode }
                            value={ type }
                            width="100%"
                            onChange={ value => handleChange(value, 'type') }
                        />
                    </FormGroup>
                </Col>
                <Col md="4" sm="6" xs="12">
                    <FormGroup label={ t('Nombre') }>
                        <Input
                            disabled={ viewMode }
                            hasError={ errors.name }
                            margin="0px"
                            value={ name }
                            onChange={ value => handleChange(value, 'name') }
                        />
                        <ErrorMessage error={ errors.name } />
                    </FormGroup>
                </Col>
                <Col md="4" sm="6" xs="12">
                    <FormGroup label={ t('Duración (minutos)') }>
                        <Input
                            disabled={ viewMode }
                            hasError={ errors.duration }
                            margin="0px"
                            type="number"
                            value={ duration }
                            onChange={ value => handleChange(value, 'duration') }
                        />
                        <ErrorMessage error={ errors.duration } />
                    </FormGroup>
                </Col>
            </Row>
            {
                appointmentTypeSpecialties &&
                <Row>
                    <>
                        <Col md="4" sm="6" xs="8">
                            <FormGroup label={ t('Especialidades') }>
                                <SpecialtyPicker
                                    disabled={ viewMode }
                                    filter={ ({ value }) => !specialty_ids.includes(value) }
                                    value={ selectedSpecialty }
                                    onChange={ setSelectedSpecialty }
                                />
                            </FormGroup>
                        </Col>
                        <Col xs="4">
                            {
                                !viewMode &&
                                <ExpandButton
                                    circle
                                    disabled={ !selectedSpecialty }
                                    icon="add"
                                    margin="20px 0px 0px 0px"
                                    size={ 40 }
                                    onClick={ addSpecialty }
                                />
                            }
                        </Col>
                    </>
                    <Col xs="12">
                        <TagList
                            items={ specialtyItems }
                            readOnly={ viewMode }
                            onRemove={ removeSpecialty }
                        />
                    </Col>
                </Row>
            }
            <Row>
                <ButtonsContainer>
                    {
                        editMode
                            ? <>
                                <ExpandButton
                                    danger
                                    caption={ t('Eliminar') }
                                    size={ 120 }
                                    onClick={ () => setModalConfirm(true) }
                                />
                                <ExpandButton
                                    caption={ t('Confirmar') }
                                    size={ 120 }
                                    onClick={ handleSubmit }
                                />
                            </>
                            : createMode
                                ? <ExpandButton
                                    caption={ t('Confirmar datos') }
                                    size={ 120 }
                                    onClick={ handleSubmit }
                                />
                                : null
                    }
                </ButtonsContainer>
            </Row>
            <ConfirmationModal
                actionHandler={ modalResponse }
                active={ modalConfirm }
                title={ t('¿Quieres dar de baja este tipo de consulta?') }
            />
        </FormLayout>
    );
};

AppointmentTypeRegistration.propTypes = {
    appointmentType: PropTypes.object,
    createAppointmentType: PropTypes.func,
    deleteAppointmentType: PropTypes.func,
    getAppointmentType: PropTypes.func,
    location: PropTypes.object,
    setTurnsAppointmentType: PropTypes.func,
    specialties: PropTypes.array,
    updateAppointmentType: PropTypes.func
};

export default AppointmentTypeRegistration;
